import type { Tutorial } from '../../shared/types.js';

export const baseBlueprintsTutorial: Tutorial = {
  name: 'base-blueprints',
  title: 'About base blueprints',
  description:
    'Understand the host-managed base every org has: what it layers, why it rebuilds, and how your own blueprints build on top of it.',
  category: 'blueprints',
  docPages: ['concepts/blueprints'],
  agenda: [
    'What the base image is',
    'Where base lives and what it holds',
    'How changes rebuild automatically',
    'Build on top of base',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: 'What is base',
      body: "Every org has exactly one host-managed blueprint named `base`. It's the foundation every other blueprint (and every agent) starts from, so you rarely build an image from scratch. Let's look at what base is made of.",
      path: '/configure/build/environments',
    },
    {
      id: 'section',
      kind: 'info',
      title: 'Where base lives',
      body: "Base sits in its own **Base** section at the top of the sidebar, kept apart from the blueprints you create. You can't edit or delete it like a normal blueprint - it's owned and maintained for you.",
      target: '[data-testid="blueprint-section-base"]',
      placement: 'right',
    },
    {
      id: 'open',
      kind: 'action',
      trigger: 'click',
      title: 'Open base',
      body: "Select **base** to open it and see exactly what it's built from.",
      target: '[data-tutorial-id="blueprint-base-item"]',
      placement: 'right',
      // Advances once the base detail has loaded - the Rebuild button only
      // renders for base, so its appearance is a reliable "we are in" signal.
      advanceWhen: '[aria-label="Rebuild base blueprint"]',
    },
    {
      id: 'layers',
      kind: 'info',
      title: 'What base is made of',
      body: 'Base stacks three layers, applied in order: the **core environment** (OS, languages, tooling), **setup commands from your installed plugins**, and your **org overrides** - the only part you edit. Together they form the foundation your other blueprints build on top of.',
      target: '[data-tutorial-id="blueprint-base-layers"]',
      placement: 'left',
    },
    {
      id: 'rebuild',
      kind: 'info',
      title: 'Changes rebuild automatically',
      body: 'Change any layer - install a plugin or edit your org overrides - and base rebuilds automatically, along with every blueprint built on it. This button forces a fresh rebuild, pulling in the latest public base image from Runloop.',
      target: '[aria-label="Rebuild base blueprint"]',
      placement: 'bottom',
    },
    {
      id: 'done',
      kind: 'info',
      title: 'Build on top of base',
      body: "That's base. When you create your own blueprints, they build on top of it, so every agent shares the same consistent foundation.",
    },
  ],
  handoff: { tutorialName: 'blueprints', label: 'Build a repo image' },
};
